"use client";

import React from "react";
import { Play, TrendingUp, Radio, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const videos = [{
  id: "vid-1",
  title: "Cutting Through Clutter to Get Breakthrough Deals",
  description: "A comprehensive cinematic campaign developed for NewMavericks' flagship partners, focusing on high-impact visual storytelling.",
  thumbnail: "https://newmavericks.com/wp-content/uploads/2026/04/Gavin-1.png?v=zctlp0hsxga",
  type: "Creative",
  icon: Sparkles,
  spanClasses: "md:col-span-2 md:row-span-2"
}, {
  id: "vid-2",
  title: "Revitalizing Startup Through Major Deals & Investors",
  description: "Highlighting disruptive technologies through dynamic motion graphics and professional direction.",
  thumbnail: "https://newmavericks.com/wp-content/uploads/2026/04/James-3.png?v=duohhryuts",
  type: "Strategy",
  icon: TrendingUp,
  spanClasses: "md:col-span-1 md:row-span-1"
}, {
  id: "vid-3",
  title: "Absolute massive improvement in so many areas",
  description: "Seamless multi-camera live broadcast execution for international corporate summits and product launches.",
  thumbnail: "https://newmavericks.com/wp-content/uploads/2026/04/levent.png?v=5vpuhy2u797",
  type: "Production",
  icon: Radio,
  spanClasses: "md:col-span-1 md:row-span-1"
}, {
  id: "vid-4",
  title: "Bringing More Business Growth Faster",
  description: "Capturing the essence of premium luxury brands with high-contrast visuals and sophisticated editing.",
  thumbnail: "https://newmavericks.com/wp-content/uploads/2026/04/Scott.png?v=eu7xed08dwi",
  type: "Creative",
  icon: Sparkles,
  spanClasses: "md:col-span-2 md:row-span-1"
}, {
  id: "vid-5",
  title: "Greater Capacity to Get Buy-In in Tough Situations",
  description: "Visualizing data-driven success stories for our clients across diverse digital landscapes.",
  thumbnail: "https://newmavericks.com/wp-content/uploads/2026/04/Jamin.png?v=4bxu6swl4p7",
  type: "Strategy",
  icon: TrendingUp,
  spanClasses: "md:col-span-1 md:row-span-1"
}, {
  id: "vid-6",
  title: "Beating Goals to Launch Rewarding Part of Career",
  description: "Deep-dive storytelling that explores the human element behind the world's most innovative brands.",
  thumbnail: "https://newmavericks.com/wp-content/uploads/2026/04/Rick.png?v=xh6vgwkoyel",
  type: "Creative",
  icon: Sparkles,
  spanClasses: "md:col-span-1 md:row-span-1"
}];
export default function VideoGallery() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="relative w-full bg-background py-24 overflow-hidden !h-[900px]" data-section-id="167393" id="videogrid">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-dark/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[30rem] h-[30rem] bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10 px-4 ![margin:-120px_0px_0px_0px] md:px-6">
        <div ref={headerRef} className={`flex flex-col items-center text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        </div>

        <div ref={gridRef} className={`grid grid-cols-3 gap-4 md:gap-6 ml-[50px] transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {videos.map((video, i) => {
          return <Dialog key={video.id}>
                <DialogTrigger asChild>
                  <div data-index={i} className={`group relative aspect-square overflow-hidden rounded-lg bg-primary-dark/20 border border-primary-dark/30 cursor-pointer transition-all duration-500 hover:border-accent/50 hover:shadow-strong`}>
                    <img src={video.thumbnail} alt={video.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />

                    <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/95 via-primary-dark/40 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-95" />

                    <div className="absolute top-4 left-4 z-10 flex gap-2"></div>

                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                      <div className="w-16 h-16 rounded-full bg-accent text-accent-dark flex items-center justify-center backdrop-blur-sm shadow-medium opacity-0 scale-75 transition-all duration-300 group-hover:opacity-100 group-hover:scale-100">
                        <Play className="w-8 h-8 ml-1 fill-current" />
                      </div>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-6 z-10 transform transition-transform duration-300 translate-y-2 group-hover:translate-y-0">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-white mb-2 leading-tight drop-shadow-lg">
                        {video.title}
                      </h3>
                    </div>
                  </div>
                </DialogTrigger>

                <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden bg-primary-dark border-border rounded-lg shadow-strong">
                  <DialogTitle className="sr-only">{video.title}</DialogTitle>
                  <DialogDescription className="sr-only">{video.description}</DialogDescription>

                  <div className="flex flex-col h-[80vh] md:flex-row">
                    <div className="relative w-full h-full bg-black flex items-center justify-center overflow-hidden group/player">
                      {i === 0 ? <iframe src="https://www.youtube.com/embed/Z3hy1jsGqlc" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full"></iframe> : i === 1 ? <iframe src="https://www.youtube.com/embed/EJtPVPdXihA" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full"></iframe> : i === 2 ? <iframe src="https://www.youtube.com/embed/HJxXry3xg74" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full"></iframe> : i === 3 ? <iframe src="https://player.vimeo.com/video/931124322?fl=pl&fe=sh" title="Vimeo video player" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="w-full h-full"></iframe> : i === 5 ? <iframe src="https://player.vimeo.com/video/948120277?fl=pl&fe=sh" title="Vimeo video player" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="w-full h-full"></iframe> : <>
                          <img src={video.thumbnail} alt={`${video.title} thumbnail`} className="absolute inset-0 w-full h-full object-cover opacity-40 blur-sm scale-110" />
                          <img src={video.thumbnail} alt={`${video.title} full`} className="relative z-10 w-full h-full object-cover" />

                          <Button size="icon" className="absolute z-20 w-20 h-20 rounded-full bg-accent text-accent-dark hover:bg-accent-hover hover:scale-110 transition-all duration-300 shadow-strong">
                            <Play className="w-10 h-10 ml-2 fill-current" />
                          </Button>
                        </>}

                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent z-20 opacity-0 group-hover/player:opacity-100 transition-opacity duration-300">
                        <div className="h-1 w-full bg-white/20 rounded-full overflow-hidden mb-4">
                          <div className="h-full bg-accent w-1/3 rounded-full" />
                        </div>
                        <div className="flex justify-between items-center text-white/80 text-sm font-sans">
                          <span>0:00 / 2:45</span>
                          <div className="flex gap-4">
                            <Sparkles className="w-5 h-5 text-white" />
                            <Radio className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>;
        })}
        </div>
      </div>
    </section>;
}